import React, { useRef, useState, useEffect, useMemo } from "react";
import { useTranslation, Trans } from "react-i18next";
import Slider from "react-slick";
import { FiChevronLeft, FiChevronRight, FiMenu, FiX } from "react-icons/fi";
import "./About.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

// Imágenes (reemplaza por tus imágenes reales)
import proyecto1 from "./images/Proyectos/Proyecto1.png";
import proyecto2 from "./images/Proyectos/Proyecto2.png";
import proyecto3 from "./images/Proyectos/Proyecto3.png";
import proyecto4 from "./images/Proyectos/Proyecto4.png";
import proyecto5 from "./images/Proyectos/Proyecto5.png";
import proyecto6 from "./images/Proyectos/Proyecto6.png";
import proyecto7 from "./images/Proyectos/Proyecto7.png";
import proyecto8 from "./images/Proyectos/Proyecto8.png";
import proyecto9 from "./images/Proyectos/Proyecto9.png";
import proyecto10 from "./images/Proyectos/Proyecto10.png";

// -------- Flechas personalizadas --------
const NextArrow = ({ className, style, onClick }) => (
  <button
    type="button"
    className={`${className} custom-arrow next`}
    style={{ ...style }}
    onClick={onClick}
    aria-label="Siguiente"
  >
    <FiChevronRight className="arrow-icon" aria-hidden="true" />
  </button>
);

const PrevArrow = ({ className, style, onClick }) => (
  <button
    type="button"
    className={`${className} custom-arrow prev`}
    style={{ ...style }}
    onClick={onClick}
    aria-label="Anterior"
  >
    <FiChevronLeft className="arrow-icon" aria-hidden="true" />
  </button>
);
// ----------------------------------------

const Nosotros = () => {
  const { t } = useTranslation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // --- Estado & refs ---
  const sliderRef = useRef(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState({});

  // --- Lista de imágenes (memo) ---
  const proyectosMemo = useMemo(
    () => [
      proyecto1, proyecto2, proyecto3, proyecto4, proyecto5,
      proyecto6, proyecto7, proyecto8, proyecto9, proyecto10
    ],
    []
  );

  // --- Configuración del carrusel (memo) ---
  const settings = useMemo(
    () => ({
      dots: true,
      infinite: true,
      speed: 500,
      slidesToShow: 1,
      slidesToScroll: 1,
      autoplay: true,
      autoplaySpeed: 4200,
      pauseOnHover: true,
      arrows: true,
      nextArrow: <NextArrow />,
      prevArrow: <PrevArrow />,
      adaptiveHeight: false,
      beforeChange: (_current, next) => setCurrentSlide(next),
      // Mejor compatibilidad táctil
      swipe: true,
      swipeToSlide: true,
      touchThreshold: 10,
      // Conservamos flechas siempre visibles
      responsive: [
        { 
          breakpoint: 768, 
          settings: {
            arrows: false,
            dots: true
          } 
        }
      ]
    }),
    []
  );

  // --- Precarga de imágenes ---
  useEffect(() => {
    let alive = true;

    const loadImage = (src) =>
      new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(src);
        img.onerror = reject;
        img.src = src;
      });

    const preloadImages = async () => {
      const loadStatus = {};
      for (let i = 0; i < proyectosMemo.length; i++) {
        try {
          await loadImage(proyectosMemo[i]);
          loadStatus[i] = true;
        } catch {
          loadStatus[i] = false;
        }
      }
      if (alive) setImagesLoaded(loadStatus);
    };

    preloadImages();
    return () => { alive = false; };
  }, [proyectosMemo]);

  // --- Bullets desde i18n ---
  const bulletsRaw = t("aboutUs.bullets", {
    returnObjects: true,
    defaultValue: ["Diseño a medida", "Instalación profesional", "Garantía y soporte", "Atención rápida"]
  });
  const bullets = Array.isArray(bulletsRaw) ? bulletsRaw : [];

  return (
    <div
      className="nosotros-wrapper"
      role="region"
      aria-label={t("aboutUs.ariaLabel", "Información sobre nuestra empresa")}
    >
      {/* Mobile Menu Button */}
      <button 
        className="mobile-menu-toggle"
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        aria-label="Abrir menú"
        aria-expanded={isMobileMenuOpen}
      >
        {isMobileMenuOpen ? <FiX /> : <FiMenu />}
      </button>

      <div className="nosotros-grid">
        {/* Columna Izquierda: Texto */}
        <section className="nosotros-info" aria-labelledby="nosotros-title">
          <span className="badge">{t("aboutUs.badge", "Nuestra Historia")}</span>

          <h1 id="nosotros-title" className="title">
            <span className="title-highlight">
              {t("aboutUs.titleHighlight", "Pasión por Transformar Espacios")}
            </span>
          </h1>

          <div className="title-divider" aria-hidden="true" />

          <p className="desc">
            <Trans
              i18nKey="aboutUs.description"
              defaults="Somos una empresa comprometida con la calidad y la innovación, dedicada a transformar espacios con soluciones modernas y personalizadas. Nuestros proyectos reflejan el esfuerzo, la dedicación y la pasión por brindar lo mejor a cada cliente."
            />
          </p>

          <ul className="bullets" aria-label={t("aboutUs.valuesAria", "Nuestros valores")}>
            {bullets.map((txt, i) => (
              <li key={i}>
                <svg viewBox="0 0 24 24" className="li-ic" aria-hidden="true">
                  <path d="M20 6L9 17l-5-5" />
                </svg>
                <span>{txt}</span>
              </li>
            ))}
          </ul>

          <div className="stats" aria-label={t("aboutUs.stats.aria", "Indicadores")}>
            <div className="stat">
              <div className="stat-num">+8</div>
              <div className="stat-label">{t("aboutUs.stats.years", "Años de experiencia")}</div>
            </div>
            <div className="stat">
              <div className="stat-num">+10.000</div>
              <div className="stat-label">{t("aboutUs.stats.projects", "Proyectos realizados")}</div>
            </div>
            <div className="stat">
              <div className="stat-num">4.5★</div>
              <div className="stat-label">{t("aboutUs.stats.rating", "Satisfacción de clientes")}</div>
            </div>
          </div>

          <div className="cta-row">
            <a
              className="btn btn-primary"
              href="https://wa.me/50660458068?text=¡Buenas!%20Vengo%20del%20sitio%20web%20y%20quisiera%20más%20información%20por%20favor.%20"
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t("aboutUs.ctaAriaPrimary","Cotizar por WhatsApp - Se abrirá en una nueva ventana")}
            >
              {t("aboutUs.ctaPrimary", "Cotizar por WhatsApp")}
            </a>
            <a
              className="btn btn-link"
              href="https://flipbooks.gruposantamaria.cr/flipbook5"
              aria-label={t("aboutUs.ctaAriaSecondary", "Ver nuestros productos")}
            >
              {t("aboutUs.ctaSecondary", "Ver productos")}
            </a>
          </div>
        </section>

        {/* Columna Derecha: Carrusel */}
        <section className="nosotros-carousel" aria-labelledby="projects-title">
          <h2 id="projects-title" className="sr-only">
            {t("aboutUs.projectsTitle", "Nuestros Proyectos")}
          </h2>

          <div className="carousel-card">
            <Slider ref={sliderRef} {...settings} className="proj-slider">
              {proyectosMemo.map((img, index) => (
                <div key={index} className="proj-slide">
                  <figure className="proj-figure">
                    {/* Contenedor con aspect-ratio + fallback */}
                    <div className={`image-container ${imagesLoaded[index] ? "loaded" : "loading"}`}>
                      <div className="image-inner">
                        <img
                          src={img}
                          alt={`${t("aboutUs.projectAlt", { defaultValue: "Proyecto de instalación" })} ${index + 1}`}
                          loading="lazy"
                          decoding="async"
                          className="proj-img"
                          sizes="(max-width: 768px) 100vw, 700px"
                          onLoad={() =>
                            setImagesLoaded((prev) => (prev[index] ? prev : { ...prev, [index]: true }))
                          }
                        />
                      </div>
                      {!imagesLoaded[index] && <div className="image-skeleton" aria-hidden="true"></div>}
                    </div>

                    <figcaption className="proj-caption">
                      <span className="cap-title">
                        {t("aboutUs.projectCaption.title", "Proyecto")} #{index + 1}
                      </span>
                      <span className="cap-sub">
                        {t("aboutUs.projectCaption.sub", "Instalación a medida")}
                      </span>
                    </figcaption>
                  </figure>
                </div>
              ))}
            </Slider>

            {/* Indicador de slide actual (decorativo) */}
            <div className="slide-indicator" aria-hidden="true">
              {currentSlide + 1} / {proyectosMemo.length}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Nosotros;